import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { Calendar, Package, AlertCircle, Clock, CheckCircle, Bell, MessageCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { listOrders } from "@/integrations/supabase/orders";
import { useAuth } from "@/components/AuthProvider";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDateBR, parseISODateAsLocal } from "@/utils/dateOnly";

interface DeliveryEvent {
  id: string;
  date: string;
  client: string;
  phone?: string | null;
  type: string;
  status: string;
  orderCode: string;
  daysUntilDelivery: number;
  isOverdue: boolean;
  isUrgent: boolean;
}

export default function Agenda() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [notifications, setNotifications] = useState<Array<{ message: string; orderCode: string }>>([]);
  const [remindersExpanded, setRemindersExpanded] = useState(false);
  const [deliveriesExpanded, setDeliveriesExpanded] = useState(false);
  const { empresa } = useAuth();
  const navigate = useNavigate();

  const { data: orders = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: listOrders,
    staleTime: 0, // Sempre frescos quando invalidados
    gcTime: 5 * 60 * 1000, // 5 minutos de cache (para evitar refetch desnecessário)
  });

  // Funções para navegação de mês
  const goToPreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const goToMonth = (monthYear: string) => {
    const [month, year] = monthYear.split('/');
    const newDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    setCurrentDate(newDate);
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Gerar opções para o seletor de mês (12 meses atrás até 12 meses à frente)
  const generateMonthOptions = () => {
    const options = [];
    const today = new Date();

    for (let i = -12; i <= 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const value = `${month}/${year}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  const monthOptions = generateMonthOptions();
  const currentMonthValue = `${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;

  // 🔧 FIX: Processar pedidos para eventos de entrega
  // Excluir pedidos que já foram entregues (Aguardando Retirada, Pronto, Entregue)
  // Mostrar apenas os que ainda precisam de ação (Em produção, Finalizando, etc)
  const deliveryEvents: DeliveryEvent[] = orders
    .filter(order => {
      const status = order.status?.toLowerCase() || '';
      return (
        order.delivery_date &&
        status !== 'cancelado' &&
        status !== 'entregue' &&
        status !== 'aguardando retirada' &&
        status !== 'pronto'
      );
    }) // Filtrar pedidos com data de entrega, não cancelados, não entregues e não prontos
    .map(order => {
      const deliveryDate = parseISODateAsLocal(order.delivery_date!);
      // Criar "hoje" apenas com data (sem horas/minutos/segundos) para comparação correta
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Também resetar deliveryDate para começar do dia (00:00:00)
      deliveryDate.setHours(0, 0, 0, 0);
      const diffTime = deliveryDate.getTime() - today.getTime();
      const daysUntilDelivery = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return {
        id: order.id,
        date: order.delivery_date!,
        client: order.customer_name,
        phone: (order as any).customer_phone ?? null,
        type: order.type,
        status: order.status,
        orderCode: order.code,
        daysUntilDelivery,
        isOverdue: daysUntilDelivery < 0,
        isUrgent: daysUntilDelivery <= 2 && daysUntilDelivery >= 0,
      };
    });

  // 🔧 FIX: Gerar notificações automáticas (sem duplicatas)
  // Filtra pedidos com status 'Entregue' ou 'Cancelado'
  // Quando um pedido é marcado como 'Entregue' em PedidoDetalhe.tsx,
  // a query é invalidada e refetch atualiza este componente automaticamente
  useEffect(() => {
    const newNotifications: Array<{ message: string; orderCode: string }> = [];
    const processedOrders = new Set<string>(); // Para evitar duplicatas
    
    deliveryEvents.forEach(event => {
      const notificationKey = `${event.orderCode}-${event.isOverdue ? 'overdue' : 'urgent'}`;
      
      if (!processedOrders.has(notificationKey)) {
        processedOrders.add(notificationKey);
        
        if (event.isOverdue) {
          newNotifications.push({
            message: `🚨 URGENTE: Pedido ${event.orderCode} (${event.client}) está atrasado!`,
            orderCode: event.orderCode
          });
        } else if (event.isUrgent) {
          newNotifications.push({
            message: `⚠️ ATENÇÃO: Pedido ${event.orderCode} (${event.client}) vence em ${event.daysUntilDelivery} dias`,
            orderCode: event.orderCode
          });
        }
      }
    });

    // Só atualizar se realmente mudou
    if (JSON.stringify(newNotifications) !== JSON.stringify(notifications)) {
      setNotifications(newNotifications);
    }
  }, [deliveryEvents, notifications]);

  const getStatusColor = (status: string, isOverdue: boolean, isUrgent: boolean) => {
    if (isOverdue) {
      return "bg-red-100 text-red-700 border-red-300";
    }
    if (isUrgent) {
      return "bg-orange-100 text-orange-700 border-orange-300";
    }
    
    switch (status) {
      case "Pronto":
        return "bg-green-100 text-green-700 border-green-300";
      case "Em produção":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "Aguardando aprovação":
        return "bg-gray-100 text-gray-700 border-gray-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getDaysUntilDeliveryText = (days: number, isOverdue: boolean) => {
    if (isOverdue) {
      return `${Math.abs(days)} dias atrasado`;
    }
    if (days === 0) {
      return "Hoje";
    }
    if (days === 1) {
      return "Amanhã";
    }
    return `${days} dias`;
  };

  const sortedEvents = [...deliveryEvents].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const sendWhatsAppReminder = async (event: DeliveryEvent) => {
    try {
      // Buscar template personalizado
      const { getWhatsAppTemplate, processTemplate, getWhatsAppSettings, addSignature, generateWhatsAppUrl } = await import("@/utils/whatsappTemplates");
      const customTemplate = empresa?.id ? await getWhatsAppTemplate(empresa.id, 'delivery') : null;
      
      let message = '';

      const normalizeWhatsAppPhone = (phone?: string | null): string | undefined => {
        if (!phone) return undefined;
        const digits = String(phone).replace(/\D/g, "");
        if (!digits) return undefined;
        if (digits.startsWith("55")) return digits;
        if (digits.length === 10 || digits.length === 11) return `55${digits}`;
        return digits;
      };
      
      // Se tem template personalizado, usar ele
      if (customTemplate) {
        message = processTemplate(customTemplate, {
          cliente: event.client,
          codigo_pedido: event.orderCode,
          data_entrega: formatDateBR(event.date),
          tipo: event.type,
          status: event.status,
          dias_restantes: event.daysUntilDelivery > 0 ? event.daysUntilDelivery.toString() : 'Atrasado'
        }, empresa);
      } else {
        // Template padrão
        message = `Olá ${event.client}!

Lembramos que seu pedido ${event.orderCode} tem entrega prevista para ${formatDateBR(event.date)}.

*DETALHES:*
• Tipo: ${event.type}
• Status: ${event.status}
• Dias restantes: ${event.daysUntilDelivery > 0 ? event.daysUntilDelivery : 'Atrasado'}

Em caso de dúvidas, entre em contato conosco!

_${empresa?.nome || 'Ateliê'}_`;
      }

      // Adicionar assinatura se configurada
      const settings = empresa?.id ? await getWhatsAppSettings(empresa.id) : null;
      message = addSignature(message, settings);

      // Gerar URL do WhatsApp
      const phone = normalizeWhatsAppPhone(event.phone) ?? normalizeWhatsAppPhone(settings?.whatsapp_number);
      const whatsappUrl = generateWhatsAppUrl(message, phone);
      window.open(whatsappUrl, '_blank');
      toast.success("Mensagem do WhatsApp preparada!");
    } catch (error) {
      console.error("Erro ao enviar lembrete WhatsApp:", error);
      toast.error("Erro ao preparar mensagem do WhatsApp");
    }
  };

  // Calendário Visual
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventColor = (event: DeliveryEvent) => {
    if (event.isOverdue) return "bg-red-100 border-red-300 text-red-700";
    if (event.isUrgent) return "bg-orange-100 border-orange-300 text-orange-700";
    if (event.status === 'Pronto') return "bg-green-100 border-green-300 text-green-700";
    if (event.status === 'Em produção') return "bg-blue-100 border-blue-300 text-blue-700";
    return "bg-gray-100 border-gray-300 text-gray-700";
  };

  const getEventsForDate = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return deliveryEvents.filter(event => event.date.startsWith(dateStr));
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const weekdayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <PageHeader
        title="Agenda de Entregas"
        description="Calendário de produção com lembretes automáticos"
        icon={<Calendar className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
      />

      <div className="p-4 md:p-8 space-y-4 md:space-y-6">
        {/* Notificações Automáticas */}
        {notifications.length > 0 && (
          <Card className="border-red-200 bg-red-50 shadow-sm">
            <CardHeader>
              <CardTitle className="text-red-800 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Lembretes Automáticos ({notifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 transition-all duration-300 ease-in-out">
                {(remindersExpanded ? notifications : notifications.slice(0, 3)).map((notification, index) => (
                  <div 
                    key={index} 
                    onClick={() => navigate(`/pedidos/${notification.orderCode}`)}
                    className="flex items-start gap-2 p-2 md:p-3 bg-white rounded border border-red-200 cursor-pointer transition-all hover:bg-red-50 hover:border-red-300"
                  >
                    <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-red-800 text-xs md:text-sm break-words">{notification.message}</span>
                  </div>
                ))}
              </div>
              {notifications.length > 3 && (
                <div className="mt-4 pt-3 border-t border-red-200">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRemindersExpanded(!remindersExpanded)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-center"
                  >
                    {remindersExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Mostrar menos
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Ver mais {notifications.length - 3} lembretes
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Resumo do Dia */}
        <div className="grid gap-3 md:gap-6 md:grid-cols-3">
          <Card className="bg-white border border-gray-200/50 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Entregas Hoje</p>
                  <p className="text-xl md:text-2xl font-bold text-blue-600">
                    {deliveryEvents.filter(e => e.daysUntilDelivery === 0).length}
                  </p>
                </div>
                <Calendar className="w-6 h-6 md:w-8 md:h-8 text-blue-500 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200/50 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Próximos 3 Dias</p>
                  <p className="text-xl md:text-2xl font-bold text-orange-600">
                    {deliveryEvents.filter(e => e.daysUntilDelivery > 0 && e.daysUntilDelivery <= 3).length}
                  </p>
                </div>
                <Clock className="w-6 h-6 md:w-8 md:h-8 text-orange-500 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200/50 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Atrasados</p>
                  <p className="text-xl md:text-2xl font-bold text-red-600">
                    {deliveryEvents.filter(e => e.isOverdue).length}
                  </p>
                </div>
                <AlertCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Entregas */}
        <Card className="bg-white border border-gray-200/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Cronograma de Entregas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedEvents.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma entrega agendada</h3>
                <p className="text-gray-600">Crie pedidos para ver o cronograma de entregas</p>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4 transition-all duration-300 ease-in-out">
                {(deliveriesExpanded ? sortedEvents : sortedEvents.slice(0, 3)).map((event) => (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/pedidos/${event.orderCode}`)}
                    className={`p-3 md:p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      event.isOverdue 
                        ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                        : event.isUrgent 
                        ? 'bg-orange-50 border-orange-200 hover:bg-orange-100' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          event.isOverdue 
                            ? 'bg-red-100 text-red-600' 
                            : event.isUrgent 
                            ? 'bg-orange-100 text-orange-600'
                            : event.status === 'Pronto'
                            ? 'bg-green-100 text-green-600'
                            : event.status === 'Em produção'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Calendar className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-semibold text-sm md:text-base text-gray-900 truncate">{event.client}</h3>
                            <Badge variant="outline" className={`text-xs ${getStatusColor(event.status, event.isOverdue, event.isUrgent)} flex-shrink-0`}>
                              {event.status}
                            </Badge>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 truncate">{event.type} - {event.orderCode}</p>
                          <p className="text-xs text-gray-500 truncate">
                            Entrega: {new Date(event.date).toLocaleDateString('pt-BR')} 
                            ({getDaysUntilDeliveryText(event.daysUntilDelivery, event.isOverdue)})
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end md:justify-start">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => sendWhatsAppReminder(event)}
                          className="text-green-600 border-green-200 hover:bg-green-50 flex-1 md:flex-none text-xs md:text-sm"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          WhatsApp
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/pedidos/${event.orderCode}`)}
                          className="flex-1 md:flex-none text-xs md:text-sm"
                        >
                          Ver Pedido
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {sortedEvents.length > 3 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeliveriesExpanded(!deliveriesExpanded)}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 w-full justify-center"
                    >
                      {deliveriesExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-2" />
                          Mostrar menos
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-2" />
                          Ver mais {sortedEvents.length - 3} entregas
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendário Visual */}
        <Card className="bg-white border border-gray-200/50 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Visão do Mês - {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousMonth}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Select value={currentMonthValue} onValueChange={goToMonth}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextMonth}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToCurrentMonth}
                  className="ml-2"
                >
                  Hoje
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Cabeçalho do Calendário - Dias da Semana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekdayNames.map(day => (
                <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Grid do Calendário */}
            <div className="grid grid-cols-7 gap-1">
              {/* Dias vazios antes do primeiro dia do mês */}
              {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}
              
              {/* Dias do mês */}
              {Array.from({ length: daysInMonth }).map((_, dayIndex) => {
                const day = dayIndex + 1;
                const events = getEventsForDate(day);
                const isToday = day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear();
                
                return (
                  <div 
                    key={day}
                    className={`aspect-square border border-gray-200 rounded-lg p-1 ${
                      isToday ? 'bg-purple-50 border-purple-300' : ''
                    }`}
                  >
                    <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-purple-700' : 'text-gray-700'}`}>
                      {day}
                    </div>
                    <div className="space-y-1">
                      {events.slice(0, 2).map((event, eventIndex) => (
                        <button
                          key={event.id}
                          onClick={() => navigate(`/pedidos/${event.orderCode}`)}
                          className={`w-full text-[10px] p-1 rounded border ${getEventColor(event)} hover:shadow-md hover:scale-105 transition-all duration-200 cursor-pointer text-left font-medium`}
                          title={`Clique para ir para: ${event.client} - ${event.orderCode}`}
                        >
                          <div className="truncate">{event.client}</div>
                          <div className="truncate text-opacity-75">{event.orderCode}</div>
                        </button>
                      ))}
                      {events.length > 2 && (
                        <div className="text-[10px] text-gray-500 font-semibold cursor-pointer hover:text-gray-700">
                          +{events.length - 2}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legenda de Cores */}
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">Legenda de Cores:</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs px-2 py-0.5">
                  🔴 Atrasados
                </Badge>
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs px-2 py-0.5">
                  🟠 Urgentes (2-3 dias)
                </Badge>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs px-2 py-0.5">
                  🔵 Em Produção
                </Badge>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs px-2 py-0.5">
                  🟢 Prontos
                </Badge>
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300 text-xs px-2 py-0.5">
                  ⚪ Outros
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}